export const SCHEMA_VERSION = 2;
export const EVENT_VERSION = 1;
// Límite por evento del contrato de POST /api/v3/events (CAM-16): api-v3 rechaza los más grandes.
export const MAX_EVENT_BYTES = 4 * 1024;

const EVENT_TYPE_PATTERN = /^[a-z]+(_[a-z0-9]+)*$/;

// El payload in-house solo lleva IDs: los nombres se quedan en PostHog. Tenant, usuario, rol y
// is_tangerine_ai los saca api-v3 del JWT; is_app va en context y el colegio, en user.school_guid.
const DROPPED_KEYS = new Set([
  'content_grade',
  'content_stage',
  'discipline',
  'is_app',
  'is_tangerine_ai',
  'name',
  'program',
  'role_guid',
  'subject',
  'tenant',
  'timestamp',
  'title',
  'user_id',
]);

export type AnalyticsScalar = string | number | boolean;
export type AnalyticsFlatRecord = Record<string, AnalyticsScalar>;
export type AnalyticsProducer = { app: string; platform: string; version: string };
export type AnalyticsUser = { language?: string; school_guid?: string };

export type AnalyticsEvent = {
  context: AnalyticsFlatRecord;
  event_id: string;
  event_type: string;
  event_version: number;
  occurred_on: string;
  payload: AnalyticsFlatRecord;
  producer: AnalyticsProducer;
  schema_version: number;
  sent_at?: string;
  session_id?: string;
  user: AnalyticsUser;
};

export type EventEnvelope = {
  context: AnalyticsFlatRecord;
  eventId: string;
  occurredOn: string;
  producer: AnalyticsProducer;
  sessionId?: string;
  user: AnalyticsUser;
};

export function isValidEventType(eventType: unknown): eventType is string {
  return typeof eventType === 'string' && EVENT_TYPE_PATTERN.test(eventType);
}

function isScalar(value: unknown): value is AnalyticsScalar {
  if (typeof value === 'number') return Number.isFinite(value);
  return typeof value === 'string' || typeof value === 'boolean';
}

function isDroppedKey(key: string): boolean {
  return DROPPED_KEYS.has(key) || key.startsWith('school_') || key.endsWith('_name');
}

export function toIdPayload(properties: unknown): AnalyticsFlatRecord {
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {};

  const payload: AnalyticsFlatRecord = {};
  Object.entries(properties).forEach(([rawKey, value]) => {
    // PostHog admite claves con espacios ('is pinned'); en la tabla de Athena serían columnas inválidas.
    const key = rawKey.trim().replace(/\s+/g, '_');
    if (key && isScalar(value) && !isDroppedKey(key)) payload[key] = value;
  });
  return payload;
}

// TextEncoder no existe en el jsdom de Jest: se cuentan los bytes UTF-8 por unidad UTF-16.
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (let index = 0; index < text.length; index++) {
    const code = text.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    // Cada mitad de un par sustituto cuenta 2: el carácter completo ocupa 4 bytes.
    else if (code >= 0xd800 && code <= 0xdfff) bytes += 2;
    else bytes += 3;
  }
  return bytes;
}

export function buildEvent(eventType: unknown, payload: unknown, envelope: EventEnvelope): AnalyticsEvent | null {
  if (!isValidEventType(eventType)) return null;

  const event: AnalyticsEvent = {
    context: envelope.context,
    event_id: envelope.eventId,
    event_type: eventType,
    event_version: EVENT_VERSION,
    occurred_on: envelope.occurredOn,
    payload: toIdPayload(payload),
    producer: envelope.producer,
    schema_version: SCHEMA_VERSION,
    user: envelope.user,
  };
  if (envelope.sessionId) event.session_id = envelope.sessionId;

  // sent_at se pone al enviar y tiene la misma longitud que occurred_on: se cuenta ya para no pasarse.
  const serialized = JSON.stringify({ ...event, sent_at: envelope.occurredOn });
  return utf8ByteLength(serialized) > MAX_EVENT_BYTES ? null : event;
}
